import { useMemo, useState } from "react";
import { PITCH_CHANNELS, PITCH_THIRDS } from "../../../lib/playerReport";

const PITCH_LENGTH = 105;
const PITCH_WIDTH = 68;
const VIEW_WIDTH = 490;
const VIEW_HEIGHT = 365;
const SCALE = VIEW_WIDTH / (PITCH_LENGTH + 2);
const OFFSET_X = SCALE;
const OFFSET_Y = (VIEW_HEIGHT - PITCH_WIDTH * SCALE) / 2;

const PENALTY_ARC = "M5.499,-7.313A9.15,9.15,0,0,1,5.499,7.313A9.15,9.15,0,0,0,5.499,-7.313Z";
const CORNER_ARC = "M0,-1A1,1,0,0,1,1,0A1,1,0,0,0,0,-1Z";

const HEAT_STOPS = [
  { at: 0, rgb: [75, 87, 98], alpha: 0 },
  { at: 0.2, rgb: [108, 120, 133], alpha: 0.26 },
  { at: 0.5, rgb: [154, 170, 184], alpha: 0.44 },
  { at: 0.8, rgb: [200, 211, 220], alpha: 0.62 },
  { at: 1, rgb: [220, 227, 233], alpha: 0.75 },
];

function heatColor(value) {
  if (value <= 0.008) return null;
  let from = HEAT_STOPS[0];
  let to = HEAT_STOPS[HEAT_STOPS.length - 1];
  for (let i = 1; i < HEAT_STOPS.length; i += 1) {
    if (value <= HEAT_STOPS[i].at) {
      from = HEAT_STOPS[i - 1];
      to = HEAT_STOPS[i];
      break;
    }
  }
  const span = to.at - from.at || 1;
  const t = Math.min(1, Math.max(0, (value - from.at) / span));
  const rgb = from.rgb.map((channel, index) => Math.round(channel + (to.rgb[index] - channel) * t));
  const alpha = from.alpha + (to.alpha - from.alpha) * t;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha.toFixed(3)})`;
}

function toScreenX(metres) {
  return OFFSET_X + metres * SCALE;
}

function toScreenY(metres) {
  return OFFSET_Y + metres * SCALE;
}

function dotRadius(count, maxStarts) {
  return 12 + 14 * Math.sqrt(count / maxStarts);
}

export default function ReportInvolvementPitch({ involvement, roleLabel }) {
  const [activeKey, setActiveKey] = useState(null);

  const { positions, heat, total, maxStarts, peakKeys, peakPositions, roleSide, seasonTotals } = involvement;

  const peakKeysSet = useMemo(() => new Set(peakKeys), [peakKeys]);
  const activePosition = positions.find((position) => position.key === activeKey) || null;
  const peakChannels = [...new Set(peakPositions.map((position) => position.channel))];
  const peakLabel =
    peakChannels.length === 1 && peakPositions.length > 1
      ? `${peakPositions.map((position) => position.lineLabel).join(" + ")} · ${peakChannels[0].charAt(0).toUpperCase()}${peakChannels[0].slice(1)}`
      : peakPositions.map((position) => position.label).join(" + ");
  const roleMatches = roleSide ? peakPositions.some((position) => position.channel === roleSide) : false;
  const summary = positions.map((position) => `${position.count} in the ${position.label.toLowerCase()}`).join(", ");

  let tooltipStyle = null;
  if (activePosition) {
    const cx = toScreenX(activePosition.x);
    const cy = toScreenY(activePosition.y);
    const left = Math.min(84, Math.max(16, (cx / VIEW_WIDTH) * 100));
    const top = (cy / VIEW_HEIGHT) * 100;
    tooltipStyle = {
      left: `${left}%`,
      top: `${top}%`,
      transform: top < 40 ? "translate(-50%, 22px)" : "translate(-50%, calc(-100% - 22px))",
    };
  }

  return (
    <div className="pr-involvement-body">
      {total === 0 ? (
        <div className="pr-involvement-empty">No played-match formation records are available for this season.</div>
      ) : (
        <>
          <div className="pr-role-strip">
            <span className="pr-role-item">
              <span className="pr-role-key">Role</span>
              <strong>{roleLabel || "Unknown"}</strong>
            </span>
            <span className="pr-role-item">
              <span className="pr-role-key">Peak area</span>
              <strong>{peakLabel}</strong>
            </span>
            {roleSide && peakPositions.length > 0 && (
              <span className={roleMatches ? "pr-role-flag" : "pr-role-flag pr-role-flag--off"}>
                {roleMatches ? "Consistent with role" : "Differs from role"}
              </span>
            )}
            <span className="pr-role-item pr-role-item--muted">
              <span className="pr-role-key">Season</span>
              <strong>{seasonTotals.appearances} apps · {seasonTotals.goals} goals · {seasonTotals.assists} assists</strong>
            </span>
          </div>

          <div className="pr-pitch-wrap">
            <svg
              viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
              className="pr-pitch"
              role="img"
              aria-label={`Formation involvement heatmap: ${summary}. ${peakPositions.length > 1 ? "Joint most involvement" : "Most involvement"} in the ${peakLabel.toLowerCase()}`}
            >
              <defs>
                <clipPath id="pr-involvement-pitch-clip">
                  <rect x="0" y="0" width={PITCH_LENGTH} height={PITCH_WIDTH} rx="1.2" ry="1.2" />
                </clipPath>
                <filter id="pr-involvement-heat-blur" x="-8%" y="-8%" width="116%" height="116%">
                  <feGaussianBlur stdDeviation="1.1" />
                </filter>
              </defs>

              <g transform={`translate(${OFFSET_X} ${OFFSET_Y}) scale(${SCALE})`} pointerEvents="none">
                <rect
                  x="0"
                  y="0"
                  width={PITCH_LENGTH}
                  height={PITCH_WIDTH}
                  rx="1.2"
                  ry="1.2"
                  fill="#171719"
                />

                <g clipPath="url(#pr-involvement-pitch-clip)">
                  <g filter="url(#pr-involvement-heat-blur)">
                    {heat.values.map((row, rowIndex) =>
                      row.map((value, columnIndex) => {
                        const color = heatColor(value);
                        if (!color) return null;
                        return (
                          <rect
                            key={`${rowIndex}-${columnIndex}`}
                            x={columnIndex * heat.cellWidth}
                            y={rowIndex * heat.cellHeight}
                            width={heat.cellWidth + 0.12}
                            height={heat.cellHeight + 0.12}
                            fill={color}
                          />
                        );
                      }),
                    )}
                  </g>

                  {PITCH_THIRDS.slice(1).map((third) => (
                    <line
                      key={`v-${third.from}`}
                      x1={third.from}
                      y1="0"
                      x2={third.from}
                      y2={PITCH_WIDTH}
                      stroke="rgba(255,255,255,0.18)"
                      strokeWidth="0.22"
                      strokeDasharray="1.2 1.2"
                    />
                  ))}
                  {PITCH_CHANNELS.slice(1).map((channel) => (
                    <line
                      key={`h-${channel.from}`}
                      x1="0"
                      y1={channel.from}
                      x2={PITCH_LENGTH}
                      y2={channel.from}
                      stroke="rgba(255,255,255,0.18)"
                      strokeWidth="0.22"
                      strokeDasharray="1.2 1.2"
                    />
                  ))}
                </g>

                <g fill="none" stroke="rgba(255,255,255,0.40)" strokeWidth="0.35" strokeLinecap="square">
                  <line x1="52.5" y1="0" x2="52.5" y2={PITCH_WIDTH} />
                  <circle cx="52.5" cy="34" r="9.15" />
                  <path d="M 0 12.92 H 16.8 V 55.08 H 0" />
                  <path d="M 0 25.84 H 6.3 V 42.16 H 0" />
                  <path d="M 105 12.92 H 88.2 V 55.08 H 105" />
                  <path d="M 105 25.84 H 98.7 V 42.16 H 105" />
                  <path d={PENALTY_ARC} transform="translate(11.235 34)" />
                  <path d={PENALTY_ARC} transform="translate(93.765 34) rotate(180)" />
                  <path d={CORNER_ARC} transform="translate(0 68)" />
                  <path d={CORNER_ARC} transform="translate(0 0) rotate(90)" />
                  <path d={CORNER_ARC} transform="translate(105 0) rotate(180)" />
                  <path d={CORNER_ARC} transform="translate(105 68) rotate(270)" />
                </g>

                <circle cx="52.5" cy="34" r="0.525" fill="rgba(255,255,255,0.40)" />
                <circle cx="11.55" cy="34" r="0.35" fill="rgba(255,255,255,0.40)" />
                <circle cx="93.45" cy="34" r="0.35" fill="rgba(255,255,255,0.40)" />

                <rect
                  x="0"
                  y="0"
                  width={PITCH_LENGTH}
                  height={PITCH_WIDTH}
                  rx="1.2"
                  ry="1.2"
                  fill="none"
                  stroke="rgba(255,255,255,0.40)"
                  strokeWidth="0.35"
                />
              </g>

              {positions.map((position) => {
                const isPeak = peakKeysSet.has(position.key);
                const isActive = position.key === activeKey;
                const cx = toScreenX(position.x);
                const cy = toScreenY(position.y);
                const share = total > 0 ? Math.round((position.count / total) * 100) : 0;
                const r = dotRadius(position.count, maxStarts);
                return (
                  <g
                    key={position.key}
                    className="pr-zone pr-zone--active"
                    onMouseEnter={() => setActiveKey(position.key)}
                    onMouseLeave={() => setActiveKey(null)}
                    onClick={() => setActiveKey((current) => (current === position.key ? null : position.key))}
                    onFocus={() => setActiveKey(position.key)}
                    onBlur={() => setActiveKey(null)}
                    tabIndex={0}
                    role="button"
                    aria-label={`${position.label}: ${position.count} starts, ${share} percent. Estimated ${position.estimatedGoals} goals, ${position.estimatedAssists} assists.`}
                  >
                    <circle cx={cx} cy={cy} r={r + 18} fill="transparent" pointerEvents="all" />
                    <g pointerEvents="none">
                      {isPeak && (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={r + 4}
                          fill="none"
                          stroke="rgba(200,211,220,0.4)"
                          strokeWidth="2"
                        />
                      )}
                      <circle
                        cx={cx}
                        cy={cy}
                        r={r}
                        fill={isActive ? "rgba(154, 170, 184, 0.35)" : "rgba(23, 23, 25, 0.93)"}
                        stroke={isActive ? "#c8d3dc" : isPeak ? "rgba(244, 244, 245, 0.98)" : "rgba(154, 170, 184, 0.85)"}
                        strokeWidth={isPeak ? 1.8 : 1.3}
                      />
                      <text x={cx} y={cy} dy="0.35em" textAnchor="middle" className="pr-dot-count">
                        {position.count}
                      </text>
                      <text x={cx} y={cy + r + 14} textAnchor="middle" className="pr-dot-share">
                        {share}%
                      </text>
                    </g>
                  </g>
                );
              })}

              <text x="127.5" y="361" textAnchor="middle" className="pr-footer-label">
                Defensive
              </text>
              <text x="362.5" y="361" textAnchor="middle" className="pr-footer-label">
                Offensive
              </text>
            </svg>

            {activePosition && (
              <div className="pr-tooltip-card" style={tooltipStyle}>
                <div className="pr-tooltip-head">
                  <span className="pr-tooltip-title">{activePosition.label}</span>
                  {peakKeysSet.has(activePosition.key) && <span className="pr-tooltip-tag">Most</span>}
                </div>
                <div className="pr-tooltip-row">
                  <span>Starts</span>
                  <strong>
                    {activePosition.count} · {Math.round(activePosition.share * 100)}%
                  </strong>
                </div>
                <div className="pr-tooltip-grid">
                  <div>
                    <span>Goals est.</span>
                    <strong>{activePosition.estimatedGoals}</strong>
                  </div>
                  <div>
                    <span>Assists est.</span>
                    <strong>{activePosition.estimatedAssists}</strong>
                  </div>
                  <div>
                    <span>Minutes est.</span>
                    <strong>{activePosition.estimatedMinutes.toLocaleString()}</strong>
                  </div>
                </div>
                <div className="pr-tooltip-sub">Positions used</div>
                <div className="pr-tooltip-tags">
                  {activePosition.formations.slice(0, 4).map((formation) => (
                    <span key={`${formation.field}-${formation.venue}`}>
                      {formation.field} <em>{formation.venue}</em> ×{formation.count}
                    </span>
                  ))}
                  {activePosition.formations.length > 4 && (
                    <span className="pr-tooltip-more">
                      +{activePosition.formations.length - 4} more
                    </span>
                  )}
                </div>
                {activePosition.recentMatches.length > 0 && (
                  <>
                    <div className="pr-tooltip-sub">Recent</div>
                    <div className="pr-tooltip-tags">
                      {activePosition.recentMatches.map((match) => (
                        <span key={`${match.opponent}-${match.date}`}>vs {match.opponent}</span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="pr-involvement-legend">
            <span>Involvement</span>
            <span className="pr-involvement-legend-meter">
              <span className="pr-involvement-legend-bar" />
              <span className="pr-involvement-legend-scale">
                <span>Low</span>
                <span>High</span>
              </span>
            </span>
          </div>
        </>
      )}
      <div className="pr-involvement-note">
        Modelled from starting formation positions, not a tracking or touch heatmap.
      </div>
    </div>
  );
}
